import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const COUNTRIES = ['Guyana', 'Jamaica', 'Bahamas', 'Cayman Islands', 'Antigua'];

const PER_UNIT_COMMISSION_JPY = 10000;
const INCENTIVE_RATE = 0.30;

const normalizeCountry = (value) => {
  if (!value) return '';
  const t = String(value).toLowerCase().trim();
  if (t.includes('jam')) return 'jamaica';
  if (t.includes('guy')) return 'guyana';
  if (t.includes('bah')) return 'bahamas';
  if (t.includes('cay')) return 'cayman islands';
  if (t.includes('ant')) return 'antigua';
  return t;
};

const money = (value, currency = 'USD') => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const yen = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return '\u00A5' + new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const AgentCalculator = () => {
  const [rules, setRules] = useState([]);
  const [country, setCountry] = useState('');
  const [costYen, setCostYen] = useState('350000');
  const [rate, setRate] = useState('153');
  const [m3, setM3] = useState('10.3');
  const [profit, setProfit] = useState('300');

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/api/public/shipping-rules`)
      .then((res) => {
        if (active) setRules(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (active) setRules([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const rule = useMemo(() => {
    const target = normalizeCountry(country);
    if (!target) return null;
    return (
      rules.find((r) => {
        const key = normalizeCountry(r.country || r.country_name || r.country_code);
        return key === target && r.is_active !== false;
      }) || null
    );
  }, [rules, country]);

  const freightRate = useMemo(() => {
    if (!rule) return 0;
    const value = rule.freight_per_m3 ?? rule.base_freight ?? 0;
    return Number(value) || 0;
  }, [rule]);

  const result = useMemo(() => {
    const cost = Number(costYen) || 0;
    const fx = Number(rate) || 0;
    const volume = Number(m3) || 0;
    const profitUsd = Number(profit) || 0;

    const fob = fx > 0 ? cost / fx : 0;
    const freight = volume * freightRate;
    const cf = fob + freight;
    const adjustedCf = cf + profitUsd;
    const commissionYen = PER_UNIT_COMMISSION_JPY;
    const incentiveYen = profitUsd * fx * INCENTIVE_RATE;
    const sumYen = commissionYen + incentiveYen;

    return { fob, freight, cf, adjustedCf, commissionYen, incentiveYen, sumYen };
  }, [costYen, rate, m3, profit, freightRate]);

  const rows = [
    { label: 'FOB (USD)', value: money(result.fob) },
    { label: 'Freight (USD)', value: freightRate ? money(result.freight) : '-' },
    { label: 'C&F (USD)', value: money(result.cf) },
    { label: 'Adjusted C&F (USD)', value: money(result.adjustedCf) },
    { label: 'Per Unit Commission', value: yen(result.commissionYen) },
    { label: 'Incentive', value: yen(result.incentiveYen) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-blue-100 rounded-lg">
          <Calculator className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Calculator</h1>
          <p className="text-sm text-gray-500">
            Estimate FOB, freight and commission for a vehicle without creating an order.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Inputs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Destination Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {!country
                  ? 'Select a country to load its freight rate.'
                  : rule
                    ? `Freight rate: ${money(freightRate)}/m3`
                    : 'No active shipping rule found for this country.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Vehicle Cost Total (JPY)</Label>
              <Input inputMode="decimal" value={costYen} onChange={(e) => setCostYen(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Exchange Rate (JPY per USD)</Label>
              <Input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>M (m3)</Label>
              <Input inputMode="decimal" value={m3} onChange={(e) => setM3(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Profit to Earn Over and Above (USD)</Label>
              <Input inputMode="decimal" value={profit} onChange={(e) => setProfit(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between border-b border-gray-100 pb-2"
              >
                <span className="text-sm text-gray-600">{row.label}</span>
                <span className="text-sm font-semibold text-gray-900">{row.value}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <span className="text-base font-bold text-gray-900">Sum of Commission + Incentive (JPY)</span>
              <span className="text-xl font-bold text-green-700">{yen(result.sumYen)}</span>
            </div>
            <p className="text-xs text-gray-400 pt-2">
              Example only (from the ERP sheet, at a 215/m3 sample rate - Cost 350,000, Rate
              153, M 10.3, Profit 300 USD): FOB 2,288 - Freight 2,215 - C&amp;F 4,502 - Adjusted C&amp;F
              4,802. Live results use the selected country&apos;s rate.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgentCalculator;
